import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, FileText, Video, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import {
  adminListBlog, adminGetBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, adminUpload,
} from '../../lib/api';

const EMPTY = { type: 'article', title: '', excerpt: '', tag: '', read_time: '', image: '', body_html: '', video_url: '', published: true };

function previewEmbed(url) {
  const u = (url || '').trim();
  if (!u) return '';
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  const lm = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (lm) return `https://www.loom.com/embed/${lm[1]}`;
  return '';
}

export default function BlogManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list view, 'new', or post id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef(null);

  const refresh = () => { setLoading(true); adminListBlog().then(setList).finally(() => setLoading(false)); };
  useEffect(() => { refresh(); }, []);

  const startNew = () => { setForm(EMPTY); setEditing('new'); };
  const startEdit = async (id) => {
    try { const post = await adminGetBlog(id); setForm({ ...EMPTY, ...post }); setEditing(id); }
    catch (e) { toast.error('Could not load post'); }
  };

  const imageHandler = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      try {
        const { fullUrl } = await adminUpload(file);
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', fullUrl, 'user');
        editor.setSelection(range.index + 1);
      } catch (e) { toast.error('Image upload failed'); }
    };
    input.click();
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link', 'image'],
        [{ align: [] }],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
  }), []);

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const { fullUrl } = await adminUpload(file); setForm((f) => ({ ...f, image: fullUrl })); }
    catch (e) { toast.error('Cover upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Add a title'); return; }
    if (form.type === 'video' && !form.video_url.trim()) { toast.error('Paste a video link'); return; }
    setSaving(true);
    try {
      if (editing === 'new') { await adminCreateBlog(form); toast.success('Post published'); }
      else { await adminUpdateBlog(editing, form); toast.success('Post updated'); }
      setEditing(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try { await adminDeleteBlog(id); toast.success('Post deleted'); refresh(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ---------- LIST VIEW ----------
  if (editing === null) {
    return (
      <div data-testid="blog-manager" className="bg-white rounded-2xl p-5 sm:p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display font-bold text-[#0E1726] text-[20px]">Blog & videos</h3>
            <p className="text-sm text-[#5B6472]">Publish written articles and short video posts.</p>
          </div>
          <button data-testid="new-post-btn" onClick={startNew} className="bg-[#2E6BFF] text-white font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2 text-sm hover:bg-[#1f5aef]">
            <Plus size={16} /> New post
          </button>
        </div>

        <div className="mt-5 divide-y divide-black/5">
          {loading && <div className="py-10 text-center text-[#5B6472]">Loading...</div>}
          {!loading && list.length === 0 && <div className="py-10 text-center text-[#5B6472]">No posts yet. Create your first one.</div>}
          {!loading && list.map((p) => (
            <div key={p.id || p.slug} data-testid={`post-row-${p.slug}`} className="py-3 flex items-center gap-3">
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${p.type === 'video' ? 'bg-[#E11D2A]/10 text-[#E11D2A]' : 'bg-[#2E6BFF]/10 text-[#2E6BFF]'}`}>
                {p.type === 'video' ? <Video size={17} /> : <FileText size={17} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#0E1726] truncate">{p.title}</div>
                <div className="text-xs text-[#5B6472] flex items-center gap-2 mt-0.5">
                  <span>{p.tag}</span><span>·</span><span>{p.read_time}</span>
                  {p.published === false && <span className="text-[#B45309] bg-[#F59E0B]/12 px-1.5 py-0.5 rounded-full font-semibold">Draft</span>}
                </div>
              </div>
              <button data-testid={`edit-${p.slug}`} onClick={() => startEdit(p.id)} className="p-2 rounded-lg text-[#5B6472] hover:bg-black/5" title="Edit"><Pencil size={16} /></button>
              <button data-testid={`delete-${p.slug}`} onClick={() => remove(p.id)} className="p-2 rounded-lg text-[#EF4444] hover:bg-[#EF4444]/10" title="Delete"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------- EDITOR VIEW ----------
  const embed = form.type === 'video' ? previewEmbed(form.video_url) : '';
  return (
    <div data-testid="blog-editor" className="bg-white rounded-2xl p-5 sm:p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
      <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-[#5B6472] hover:text-[#2E6BFF] text-sm font-medium"><ArrowLeft size={16} /> Back to posts</button>

      <div className="mt-4 inline-flex bg-[#F4F4F7] rounded-full p-1">
        {[{ k: 'article', label: 'Written article', icon: FileText }, { k: 'video', label: 'Video post', icon: Video }].map((t) => (
          <button key={t.k} data-testid={`type-${t.k}`} onClick={() => setForm((f) => ({ ...f, type: t.k }))}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${form.type === t.k ? 'bg-white text-[#0E1726] shadow-sm' : 'text-[#5B6472]'}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <label className="text-sm font-semibold text-[#0E1726]">Title</label>
          <input data-testid="field-title" value={form.title} onChange={set('title')} placeholder="A clear, compelling title" className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#0E1726]">Excerpt {form.type === 'video' && <span className="text-[#5B6472] font-normal">(shown under the video)</span>}</label>
          <textarea data-testid="field-excerpt" value={form.excerpt} onChange={set('excerpt')} rows={2} placeholder="A one or two line summary" className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-[#0E1726]">Tag / category</label>
            <input data-testid="field-tag" value={form.tag} onChange={set('tag')} placeholder="e.g. Product Craft" className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#0E1726]">Read/watch time <span className="text-[#5B6472] font-normal">(optional)</span></label>
            <input data-testid="field-readtime" value={form.read_time} onChange={set('read_time')} placeholder={form.type === 'video' ? '2 min watch' : 'auto'} className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-[#0E1726]">Cover image {form.type === 'video' && <span className="text-[#5B6472] font-normal">(optional — falls back to the video thumbnail)</span>}</label>
          <div className="mt-1.5 flex items-center gap-3 flex-wrap">
            {form.image && <img src={form.image} alt="cover" className="w-24 h-16 rounded-lg object-cover border border-black/10" />}
            <label className="cursor-pointer bg-[#F4F4F7] hover:bg-black/5 text-[#0E1726] text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {form.image ? 'Replace' : 'Upload'} image
              <input data-testid="cover-upload" type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files && e.target.files[0])} />
            </label>
            {form.image && <button onClick={() => setForm((f) => ({ ...f, image: '' }))} className="text-sm text-[#EF4444] font-medium">Remove</button>}
          </div>
        </div>

        {form.type === 'article' ? (
          <div data-testid="rich-editor">
            <label className="text-sm font-semibold text-[#0E1726]">Article body</label>
            <div className="mt-1.5 rounded-xl overflow-hidden border border-black/10">
              <ReactQuill ref={quillRef} theme="snow" value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} modules={modules} placeholder="Write your article..." />
            </div>
          </div>
        ) : (
          <div>
            <label className="text-sm font-semibold text-[#0E1726]">Video link <span className="text-[#5B6472] font-normal">(YouTube, Vimeo or Loom)</span></label>
            <input data-testid="field-video-url" value={form.video_url} onChange={set('video_url')} placeholder="https://youtube.com/watch?v=..." className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
            {embed ? (
              <div className="mt-3 rounded-xl overflow-hidden bg-black">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe title="preview" src={embed} className="absolute inset-0 w-full h-full" frameBorder="0" allowFullScreen />
                </div>
              </div>
            ) : form.video_url.trim() ? (
              <p className="text-xs text-[#B45309] mt-2">Couldn't recognise this link. We support YouTube, Vimeo and Loom.</p>
            ) : null}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-black/5">
          <button data-testid="toggle-publish" onClick={() => setForm((f) => ({ ...f, published: !f.published }))} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0E1726]">
            {form.published ? <Eye size={16} className="text-[#059669]" /> : <EyeOff size={16} className="text-[#B45309]" />}
            {form.published ? 'Published (visible on site)' : 'Draft (hidden)'}
          </button>
          <button data-testid="save-post-btn" onClick={save} disabled={saving} className="bg-[#2E6BFF] text-white font-semibold px-6 py-2.5 rounded-full inline-flex items-center gap-2 hover:bg-[#1f5aef] disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />} {editing === 'new' ? 'Publish post' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
